import { z } from 'zod';

import authService from '@/services/auth/auth';

import { statusSchema } from './common';
import { apiRoutes } from './config';

const analysisSectionSchema = z.object({
  score: z.number(),
  name: z.string(),
  comment: z.string(),
  missing: z.array(z.string()),
  suggestions: z.array(z.string()),
});

const hardSkillSchema = z.object({
  name: z.string(),
  keywords: z.array(z.string()),
  mustHave: z.boolean(),
  importance: z.number(),
  description: z.string().optional(),
});

const softSkillSchema = z.object({
  name: z.string(),
  mustHave: z.boolean(),
  importance: z.number(),
  description: z.string().optional(),
});

const languageSchema = z.object({
  level: z.string().nullable(),
  language: z.string(),
  mustHave: z.boolean(),
});

const educationSchema = z.object({
  level: z.string(),
  mustHave: z.boolean(),
});

const certificateSchema = z.object({
  name: z.string(),
  mustHave: z.boolean(),
});

export const offerSchema = z.object({
  id: z.number(),
  isMatched: z.boolean(),
  matchId: z.number().nullable(),
  status: statusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  company: z.string().nullable(),
  jobTitle: z.string().nullable(),
  originalText: z.string(),
  skillsHard: z.array(hardSkillSchema),
  skillsSoft: z.array(softSkillSchema),
  languages: z.array(languageSchema),
  education: z.array(educationSchema),
  certificates: z.array(certificateSchema),
  experienceYears: z.object({
    min: z.number().nullable(),
    preferred: z.number().nullable(),
  }),
  location: z.object({
    city: z.string().nullable(),
    workMode: z.string().nullable(),
  }),
  employment: z.object({
    workload: z.string().nullable(),
    contractType: z.string().nullable(),
  }),
  salary: z.object({
    currency: z.string().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    netGross: z.string().nullable(),
    period: z.string().nullable(),
  }),
  otherRequirements: z.array(z.string()),
  generatedBy: z.number(),
});
export type ResumeAnalysisOffer = z.infer<typeof offerSchema>;

export const resumeToOfferAnalysisSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  status: statusSchema,
  errorMessage: z.string().nullable(),
  sections: z.array(analysisSectionSchema),
  jobOffer: offerSchema,
});

export const getResumeToOfferAnalysis = async (id: number) => {
  const { data } = await authService.get(apiRoutes.resume.match.detail(id));
  return resumeToOfferAnalysisSchema.parse(data);
};

export const getJobOffersToAnalysis = async ({
  resumeId,
}: {
  resumeId: number;
}) => {
  const { data } = await authService.get(apiRoutes.resume.jobOffer._root, {
    params: {
      resume_id: resumeId,
    },
  });
  return z.array(offerSchema).parse(data);
};

export const postJobOfferToAnalysis = async ({ offer }: { offer: string }) =>
  authService.post(apiRoutes.resume.jobOffer._root, { offer });

export const startMatchingResumeToOffer = ({
  jobOfferId,
  resumeId,
}: {
  resumeId: number;
  jobOfferId: number;
}) =>
  authService.post(apiRoutes.resume.match._root, {
    offer: jobOfferId,
    resume: resumeId,
  });
